import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Loader2,
  Trash2,
  Mail,
  Paperclip,
  FileText,
  Database,
  Cloud,
  Sparkles,
  ArrowRight,
  Users,
  Shield,
  GitMerge,
  AlertTriangle,
} from 'lucide-react';
import apiService from '../services/api';
import logger from '../utils/logger';

interface ReprocessModalProps {
  isOpen: boolean;
  caseNumber: string;
  onClose: (wasReprocessCompleted?: boolean) => void;
  onSuccess: () => void;
}

// All possible stages in the reprocess workflow
type ReprocessStage =
  | 'idle'
  | 'user_info'
  | 'deleting'
  | 'pulling_dynamics'
  | 'saving_case'
  | 'fetching_employees'
  | 'fetching_dashboard_notes'
  | 'fetching_dashboard_notes'
  | 'fetching_pay_item_template'
  | 'downloading_emails'
  | 'downloading_attachments'
  | 'ocr_llm_processing'
  | 'ocr'
  | 'llm_extraction'
  | 'consolidation'
  | 'done'
  | 'error';

interface StageInfo {
  key: ReprocessStage;
  label: string;
  description: string;
  icon: React.ReactNode;
}

// Ordered list of stages for display
const STAGES: StageInfo[] = [
  {
    key: 'user_info',
    label: 'Checking Core Authorization',
    description: 'Validating Core API permissions',
    icon: <Shield className="w-5 h-5" />,
  },
  {
    key: 'deleting',
    label: 'Delete Existing Data',
    description: 'Removing old case data',
    icon: <Trash2 className="w-5 h-5" />,
  },
  {
    key: 'pulling_dynamics',
    label: 'Fetch from Dynamics',
    description: 'Pulling case from Dynamics 365',
    icon: <Cloud className="w-5 h-5" />,
  },
  {
    key: 'saving_case',
    label: 'Save Case',
    description: 'Saving case to database',
    icon: <Database className="w-5 h-5" />,
  },
  {
    key: 'fetching_employees',
    label: 'Fetch Employees',
    description: 'Calling Core API for employees',
    icon: <Users className="w-5 h-5" />,
  },
  {
    key: 'fetching_dashboard_notes',
    label: 'Fetch Dashboard Notes',
    description: 'Calling Core API for dashboard notes',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    key: 'fetching_pay_item_template',
    label: 'Pay Item Template',
    description: 'Fetching pay item details from Core',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    key: 'downloading_emails',
    label: 'Download Emails',
    description: 'Fetching emails from Dynamics',
    icon: <Mail className="w-5 h-5" />,
  },
  {
    key: 'downloading_attachments',
    label: 'Download Attachments',
    description: 'Downloading attachment files',
    icon: <Paperclip className="w-5 h-5" />,
  },
  {
    key: 'ocr_llm_processing',
    label: 'OCR & Extraction',
    description: 'Processing documents with OCR + AI',
    icon: <Sparkles className="w-5 h-5" />,
  },
  {
    key: 'consolidation',
    label: 'Consolidating Results',
    description: 'Merging attachment and email data',
    icon: <GitMerge className="w-5 h-5" />,
  },
];

interface StageStatus {
  status: 'pending' | 'active' | 'complete' | 'error' | 'warning';
  data?: Record<string, any>;
  error?: string;
}

const ReprocessModal: React.FC<ReprocessModalProps> = ({
  isOpen,
  caseNumber,
  onClose,
  onSuccess,
}) => {
  const [currentStage, setCurrentStage] = useState<ReprocessStage>('idle');
  const [stageStatuses, setStageStatuses] = useState<Record<string, StageStatus>>({});
  const [currentMessage, setCurrentMessage] = useState('Ready to reprocess case');
  const [error, setError] = useState<string | null>(null);
  const [isBlockError, setIsBlockError] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [blockType, setBlockType] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState<{
    current: number;
    total: number;
    name: string;
  } | null>(null);
  const [ocrSubMessage, setOcrSubMessage] = useState<string | null>(null);
  const [summary, setSummary] = useState<Record<string, any> | null>(null);
  const [pipelineRetry, setPipelineRetry] = useState<{
    attempt: number;
    maxAttempts: number;
  } | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const hasStartedRef = useRef(false);
  const wasOpenRef = useRef(false);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Calculate progress percentage
  const progressInfo = useMemo(() => {
    const stageKeys: Set<string> = new Set(STAGES.map((s) => s.key));
    const completedCount = Object.entries(stageStatuses).filter(
      ([key, s]) => stageKeys.has(key) && s.status === 'complete'
    ).length;
    let baseProgress = completedCount / STAGES.length;
    if (
      ocrProgress &&
      ocrProgress.total > 0 &&
      stageStatuses['ocr_llm_processing']?.status !== 'complete'
    ) {
      const ocrFraction = (ocrProgress.current - 1) / ocrProgress.total;
      baseProgress = completedCount / STAGES.length + ocrFraction / STAGES.length;
    }
    const percentage = Math.min(100, Math.round(baseProgress * 100));
    const currentStageIndex = STAGES.findIndex(
      (s) => stageStatuses[s.key]?.status === 'active'
    );
    return {
      completedCount,
      percentage,
      currentStageIndex: currentStageIndex >= 0 ? currentStageIndex + 1 : 0,
    };
  }, [stageStatuses, ocrProgress]);

  // Auto-scroll to bottom only when a stage completes (not on every state change)
  useEffect(() => {
    if (contentScrollRef.current && progressInfo.completedCount > 0) {
      contentScrollRef.current.scrollTop = contentScrollRef.current.scrollHeight;
    }
  }, [progressInfo.completedCount]);

  // Reset state only when modal transitions from closed to open (not on every re-render)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - reset state for new reprocess
      setCurrentStage('idle');
      setStageStatuses({});
      setCurrentMessage('Ready to reprocess case');
      setError(null);
      setIsBlockError(false);
      setIsAuthError(false);
      setBlockType(null);
      setIsProcessing(false);
      setOcrProgress(null);
      setOcrSubMessage(null);
      setSummary(null);
      hasStartedRef.current = false;
    }

    // Cleanup on close
    if (!isOpen && eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      hasStartedRef.current = false;
    }

    // Track previous open state
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const updateStageStatus = (
    stage: string,
    status: StageStatus['status'],
    data?: Record<string, any>,
    errorMsg?: string
  ) => {
    setStageStatuses((prev) => ({
      ...prev,
      [stage]: { status, data, error: errorMsg },
    }));
  };

  const startReprocess = async () => {
    if (hasStartedRef.current || isProcessing) {
      logger.debug('Reprocess already in progress');
      return;
    }

    hasStartedRef.current = true;
    setIsProcessing(true);
    setError(null);
    setCurrentStage('deleting');
    setCurrentMessage('Starting reprocess...');

    // Initialize all stages as pending
    const initialStatuses: Record<string, StageStatus> = {};
    STAGES.forEach((s) => {
      initialStatuses[s.key] = { status: 'pending' };
    });
    setStageStatuses(initialStatuses);

    try {
      // Create SSE connection using centralized URL from API service
      const url = apiService.getReprocessCaseStreamUrl(caseNumber);

      logger.debug('[ReprocessModal] Connecting to SSE');

      const eventSource = new EventSource(url, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          const {
            stage,
            status,
            data: eventData,
            error: eventError,
            isAuthError: authError,
          } = data;

          // Update current stage
          if (stage && stage !== 'error') {
            setCurrentStage(stage as ReprocessStage);
          }

          // Find stage info for message
          const stageInfo = STAGES.find((s) => s.key === stage);

          // Handle pipeline retry (zero employees found)
          if (stage === 'pipeline_retry' && status === 'started') {
            setPipelineRetry({
              attempt: eventData?.attempt || 2,
              maxAttempts: eventData?.maxAttempts || 3,
            });
            setStageStatuses((prev) => {
              const updated = { ...prev };
              delete updated['ocr_llm_processing'];
              delete updated['consolidation'];
              return updated;
            });
            setOcrProgress(null);
            setOcrSubMessage(null);
          } else if (status === 'started') {
            if (stageInfo) {
              setCurrentMessage(stageInfo.description);
              updateStageStatus(stage, 'active');
            }

            // Special handling for OCR per-attachment progress
            if (stage === 'ocr' || stage === 'llm_extraction') {
              if (eventData?.attachment_index && eventData?.attachments_total) {
                setOcrProgress({
                  current: eventData.attachment_index,
                  total: eventData.attachments_total,
                  name:
                    eventData.attachment_name ||
                    `Attachment ${eventData.attachment_index}`,
                });
              }
            }

            // Update sub-step message if provided
            if (eventData?.message) {
              setOcrSubMessage(eventData.message);
            }

            // Email-only extraction: show message instead of attachment progress
            if (stage === 'ocr_llm_processing' && eventData?.attachments_count === 0) {
              setOcrProgress(null);
              setOcrSubMessage(eventData?.message || 'Analyzing email content...');
            }
          } else if (status === 'complete') {
            updateStageStatus(stage, 'complete', eventData);

            if (stage === 'ocr_llm_processing' || stage === 'llm_extraction') {
              setOcrProgress(null);
              setOcrSubMessage(null);
            }

            // Handle completion
            if (stage === 'done') {
              setCurrentStage('done');
              setCurrentMessage('Reprocess complete!');
              setSummary(eventData);
              eventSource.close();
              eventSourceRef.current = null;
              setIsProcessing(false);
              // Note: onSuccess() is called when user closes the modal (see handleClose)
            }
          } else if (status === 'error') {
            // Compute error message first so we can pass it to stage status
            const errorMessage = eventError || eventData?.error || 'An error occurred';
            updateStageStatus(stage, 'error', undefined, errorMessage);
            setError(errorMessage);
            // Check if this is an auth error (not authorized for Core API)
            if (authError) {
              setIsAuthError(true);
            }
            // Check if this is a business rule block error
            // isBlockError is in eventData (which is data.data from the SSE event)
            if (eventData?.isBlockError) {
              setIsBlockError(true);
              setBlockType(eventData?.blockType || null);
            }
            setCurrentStage('error');
            setCurrentMessage('Reprocess failed');
            eventSource.close();
            eventSourceRef.current = null;
            setIsProcessing(false);
          } else if (status === 'warning') {
            // Non-fatal error (e.g., one attachment failed but others continue)
            updateStageStatus(stage, 'warning', eventData, eventError);
          }
        } catch (parseError) {
          logger.error('[ReprocessModal] Failed to parse SSE event:', parseError);
        }
      };

      eventSource.onerror = (err) => {
        logger.error('[ReprocessModal] SSE error:', err);
        eventSource.close();
        eventSourceRef.current = null;

        if (currentStage !== 'done') {
          setError('Connection lost. Please try again.');
          setCurrentStage('error');
          setCurrentMessage('Connection failed');
          setIsProcessing(false);
        }
      };
    } catch (err: any) {
      logger.error('[ReprocessModal] Failed to start reprocess:', err);
      setError(err.message || 'Failed to start reprocess');
      setCurrentStage('error');
      setCurrentMessage('Failed to start');
      setIsProcessing(false);
    }
  };

  const handleRetry = () => {
    hasStartedRef.current = false;
    setError(null);
    startReprocess();
  };

  const handleClose = () => {
    if (isProcessing && currentStage !== 'done' && currentStage !== 'error') {
      // Don't allow closing during active processing
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const wasSuccessful = currentStage === 'done';
    const wasError = currentStage === 'error';

    // Call onSuccess to refresh data if reprocess completed OR if there was an error
    // (error case needs refresh to show the block banner)
    if (wasSuccessful || wasError) {
      onSuccess();
    }

    onClose(wasSuccessful);
  };

  const canClose = !isProcessing || currentStage === 'done' || currentStage === 'error';

  const getStageIcon = (stage: StageInfo, index: number) => {
    const status = stageStatuses[stage.key]?.status || 'pending';

    switch (status) {
      case 'active':
        return (
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-400 flex items-center justify-center shadow-lg shadow-amber-200">
              <Loader2 className="w-5 h-5 text-amber-800 animate-spin" />
            </div>
            <div className="absolute -inset-1 rounded-full bg-amber-300 opacity-30 animate-ping" />
          </div>
        );
      case 'complete':
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-200">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
        );
      case 'error':
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-200">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
        );
      case 'warning':
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shadow-lg shadow-amber-200">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-full bg-neutral-100 border-2 border-neutral-200 flex items-center justify-center text-neutral-400">
            <span className="text-sm font-semibold">{index + 1}</span>
          </div>
        );
    }
  };

  const getStageRowClass = (stageKey: string) => {
    const status = stageStatuses[stageKey]?.status || 'pending';

    switch (status) {
      case 'active':
        return 'bg-gradient-to-r from-amber-50 to-amber-100 border-l-4 border-l-amber-400 border-y border-r border-amber-200 shadow-sm';
      case 'complete':
        return 'bg-gradient-to-r from-emerald-50 to-green-50 border-l-4 border-l-emerald-500 border-y border-r border-emerald-100';
      case 'error':
        return 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-l-red-500 border-y border-r border-red-100';
      case 'warning':
        return 'bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-l-amber-500 border-y border-r border-amber-100';
      default:
        return 'bg-neutral-50/50 border-l-4 border-l-neutral-200 border-y border-r border-neutral-100 opacity-60';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header with Gradient */}
        <div className="bg-gradient-to-r from-amber-300 to-amber-400 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-xl bg-amber-200/50 backdrop-blur flex items-center justify-center">
                {currentStage === 'done' ? (
                  <CheckCircle className="w-7 h-7 text-emerald-600" />
                ) : currentStage === 'error' ? (
                  <AlertCircle className="w-7 h-7 text-red-600" />
                ) : (
                  <RefreshCw
                    className={`w-7 h-7 text-amber-700 ${isProcessing ? 'animate-spin' : ''}`}
                  />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-amber-900">
                  {currentStage === 'done'
                    ? 'Reprocess Complete'
                    : currentStage === 'error'
                      ? 'Reprocess Failed'
                      : 'Reprocess Case'}
                </h2>
                <p className="text-amber-700 text-sm font-medium">{caseNumber}</p>
              </div>
            </div>
            {canClose && (
              <button
                onClick={handleClose}
                className="text-amber-700 hover:text-amber-900 transition-colors p-2 hover:bg-amber-200/50 rounded-lg"
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Progress Bar */}
          {currentStage !== 'idle' &&
            currentStage !== 'done' &&
            currentStage !== 'error' && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-amber-700">
                    Step{' '}
                    {progressInfo.currentStageIndex || progressInfo.completedCount + 1}{' '}
                    of {STAGES.length}
                  </span>
                  <span className="text-amber-900 font-semibold">
                    {progressInfo.percentage}%
                  </span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-green-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${progressInfo.percentage}%` }}
                  />
                </div>
              </div>
            )}
        </div>

        {/* Sticky Status Message */}
        {currentStage !== 'idle' &&
          currentStage !== 'done' &&
          currentStage !== 'error' && (
            <div className="px-6 pt-4 pb-3 bg-neutral-50/50 border-b border-neutral-200">
              <div className="p-4 bg-white rounded-xl border border-neutral-200 shadow-sm">
                <p className="text-sm font-semibold text-neutral-800">
                  {currentMessage}
                </p>
                {ocrProgress && (
                  <div className="mt-2">
                    <p className="text-xs text-neutral-500">
                      Processing attachment {ocrProgress.current} of {ocrProgress.total}
                    </p>
                    <p className="text-xs text-amber-500 font-medium truncate">
                      {ocrProgress.name}
                    </p>
                  </div>
                )}
                {!ocrProgress && ocrSubMessage && (
                  <p className="mt-2 text-xs text-amber-500 font-medium">
                    {ocrSubMessage}
                  </p>
                )}
              </div>
            </div>
          )}

        {/* Content */}
        <div
          ref={contentScrollRef}
          className="px-6 py-5 overflow-y-auto flex-1 bg-neutral-50/50"
        >
          {/* Start Button (before processing) */}
          {currentStage === 'idle' && (
            <div className="mb-5 p-5 rounded-xl border-2 border-dashed border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <RefreshCw className="w-6 h-6 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-neutral-800 mb-2">
                    Ready to Reprocess
                  </h3>
                  <p className="text-sm text-neutral-600 mb-4">
                    This will delete all existing data for this case and re-pull
                    everything from Dynamics, including emails, attachments, and
                    re-running OCR and AI extraction.
                  </p>
                  <button
                    onClick={startReprocess}
                    className="w-full bg-gradient-to-r from-amber-300 to-amber-400 text-amber-900 py-3 px-4 rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all font-semibold flex items-center justify-center space-x-2 shadow-lg shadow-amber-200"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Start Reprocess</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pipeline Retry Banner */}
          {pipelineRetry && (
            <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-xl animate-pulse">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold text-amber-800">
                    Retry {pipelineRetry.attempt} of {pipelineRetry.maxAttempts}
                  </p>
                  <p className="text-base font-semibold text-amber-700">
                    No employees found — retrying extraction pipeline
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Progress Stages */}
          {currentStage !== 'idle' && (
            <div className="space-y-3">
              {STAGES.map((stage, index) => (
                <div
                  key={stage.key}
                  className={`flex items-center space-x-4 p-4 rounded-xl transition-all duration-300 ${getStageRowClass(stage.key)}`}
                >
                  {getStageIcon(stage, index)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-semibold text-neutral-800">
                        {stage.label}
                      </p>
                      {stageStatuses[stage.key]?.status === 'complete' && (
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                      )}
                    </div>
                    {stageStatuses[stage.key]?.data && (
                      <p className="text-xs text-neutral-600 mt-0.5 truncate">
                        {formatStageData(stage.key, stageStatuses[stage.key].data)}
                      </p>
                    )}
                    {stageStatuses[stage.key]?.error && (
                      <p className="text-xs text-red-600 mt-0.5 whitespace-pre-line">
                        {stageStatuses[stage.key].error}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error State with Retry (no retry for block/auth errors) */}
          {currentStage === 'error' && error && (
            <div
              className={`mt-5 bg-gradient-to-r ${isAuthError ? 'from-amber-50 to-yellow-50 border-amber-200' : 'from-red-50 to-rose-50 border-red-200'} border rounded-xl p-5`}
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`w-12 h-12 rounded-xl ${isAuthError ? 'bg-amber-100' : 'bg-red-100'} flex items-center justify-center flex-shrink-0`}
                >
                  <AlertCircle
                    className={`w-6 h-6 ${isAuthError ? 'text-amber-600' : 'text-red-600'}`}
                  />
                </div>
                <div className="flex-1">
                  <h3
                    className={`font-bold ${isAuthError ? 'text-amber-900' : 'text-red-900'} mb-1`}
                  >
                    {isAuthError
                      ? 'Not Authorized'
                      : isBlockError
                        ? 'Case Blocked'
                        : 'Reprocess Failed'}
                  </h3>
                  <p
                    className={`text-sm ${isAuthError ? 'text-amber-700' : 'text-red-700'} mb-4 whitespace-pre-line`}
                  >
                    {error}
                  </p>
                  {isAuthError ? (
                    <p className="text-xs text-amber-600 mb-4">
                      Your account does not have Core API access. Please contact your
                      administrator.
                    </p>
                  ) : isBlockError ? (
                    <p className="text-xs text-red-600 mb-4">
                      This case cannot be processed due to business rule violations.
                      Close this dialog to view the case details.
                    </p>
                  ) : (
                    <button
                      onClick={handleRetry}
                      className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:from-red-700 hover:to-rose-700 transition-all text-sm font-semibold shadow-lg shadow-red-200"
                    >
                      Try Again
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Success Summary */}
          {currentStage === 'done' && summary && (
            <div className="mt-5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-5">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-200">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-emerald-900 text-lg mb-3">
                    Reprocess Complete!
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {summary.emails_processed || 0}
                      </p>
                      <p className="text-xs text-neutral-600 font-medium">Emails</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {summary.attachments_downloaded || 0}
                      </p>
                      <p className="text-xs text-neutral-600 font-medium">
                        Attachments
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-emerald-100 text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {summary.attachments_ocr_processed || 0}
                      </p>
                      <p className="text-xs text-neutral-600 font-medium">
                        OCR Processed
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {canClose && (
          <div className="px-6 py-4 border-t border-neutral-200 bg-white flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                currentStage === 'done'
                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 shadow-lg shadow-emerald-200'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              <span>
                {currentStage === 'done'
                  ? 'Continue to Case'
                  : currentStage === 'error'
                    ? 'Close'
                    : 'Cancel'}
              </span>
              {currentStage === 'done' && <ArrowRight className="w-5 h-5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper to format stage-specific data for display
function formatStageData(stage: string, data?: Record<string, any>): string {
  if (!data) return '';

  switch (stage) {
    case 'deleting':
      return data.deleted ? 'Deleted existing data' : 'No existing data found';
    case 'pulling_dynamics':
      return data.title || '';
    case 'saving_case':
      return `Case ${data.case_number}`;
    case 'fetching_employees':
      if (data.warning) return data.warning;
      return `${data.employees_count || 0} employees fetched`;
    case 'fetching_dashboard_notes':
      if (data.warning) return data.warning;
      return `${data.notes_count || 0} notes fetched`;
    case 'fetching_dashboard_notes':
      if (data.warning) return data.warning;
      return `${data.notes_count || 0} notes fetched`;
    case 'fetching_pay_item_template':
      if (data.skipped) return 'Skipped';
      if (data.warning) return data.warning;
      return `${data.columns_count || 0} pay items${data.template_name ? ` (${data.template_name})` : ''}`;
    case 'downloading_emails':
      return `${data.emails_count || 0} emails downloaded`;
    case 'downloading_attachments':
      return `${data.attachments_downloaded || 0} downloaded, ${data.attachments_failed || 0} failed`;
    case 'ocr_llm_processing':
      if (data.skipped) return data.reason || 'No attachments';
      if (data.email_extraction)
        return `${data.employees_extracted || 0} employees extracted from emails`;
      return `${data.attachments_processed || data.attachments_count || 0} attachments processed`;
    case 'consolidation':
      if (data.skipped) return data.message || 'Skipped';
      return `${data.employees_consolidated || 0} employees consolidated`;
    default:
      return '';
  }
}

export default ReprocessModal;
